// components/CalendarSection.js - Version corrigée avec code couleur et tooltips
import { AlertCircle, Calendar, ChevronLeft, ChevronRight, Target, RefreshCw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useCalendarDataCached } from '../../../hooks-back/useCalendarDataCached';
import CalendarFilters from './CalendarFilters';

const CalendarSection = ({
    showFilters = false,
    className = "",
    // NOUVEAU : Recevoir les données centralisées
    data,
    hooks,
    onGlobalRefresh,
    filterProps
}) => {
    // Extraire les props de filtres si disponibles
    const {
        filterByMonth,
        filterByCategory,
        filterByType,
        filterByStatus,
        filterByPriority,
        onFilterMonthChange,
        onFilterCategoryChange,
        onFilterTypeChange,
        onFilterStatusChange,
        onFilterPriorityChange
    } = filterProps || {};

    // États des filtres locaux (fallback si pas de filterProps)
    const [localFilterByMonth, setLocalFilterByMonth] = useState('all');
    const [localFilterByCategory, setLocalFilterByCategory] = useState('all');
    const [localFilterByType, setLocalFilterByType] = useState('all');
    const [localFilterByStatus, setLocalFilterByStatus] = useState('all');
    const [localFilterByPriority, setLocalFilterByPriority] = useState('all');

    // État pour le tooltip
    const [hoveredEvent, setHoveredEvent] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    // Utiliser les filtres centralisés ou locaux
    const activeFilterByMonth = filterByMonth || localFilterByMonth;
    const activeFilterByCategory = filterByCategory || localFilterByCategory;
    const activeFilterByType = filterByType || localFilterByType;
    const activeFilterByStatus = filterByStatus || localFilterByStatus;
    const activeFilterByPriority = filterByPriority || localFilterByPriority;

    // État pour la navigation du calendrier
    const [currentDate, setCurrentDate] = useState(new Date());

    // 🎨 Fonction pour obtenir les couleurs selon le statut
    const getStatusColors = useCallback((status) => {
        const statusColors = {
            'a_faire': {
                background: '#fef3c7', // jaune très clair
                border: '#f59e0b',      // jaune/orange
                text: '#92400e'         // brun foncé
            },
            'À faire': {
                background: '#fef3c7',
                border: '#f59e0b',
                text: '#92400e'
            },
            'en_cours': {
                background: '#dbeafe', // bleu très clair
                border: '#3b82f6',     // bleu
                text: '#1e40af'        // bleu foncé
            },
            'En cours': {
                background: '#dbeafe',
                border: '#3b82f6',
                text: '#1e40af'
            },
            'fait': {
                background: '#d1fae5', // vert très clair
                border: '#10b981',     // vert
                text: '#065f46'        // vert foncé
            },
            'Fait': {
                background: '#d1fae5',
                border: '#10b981',
                text: '#065f46'
            },
            'annule': {
                background: '#fee2e2', // rouge très clair
                border: '#ef4444',     // rouge
                text: '#991b1b'        // rouge foncé
            },
            'Annulé': {
                background: '#fee2e2',
                border: '#ef4444',
                text: '#991b1b'
            }
        };

        return statusColors[status] || {
            background: '#f3f4f6',
            border: '#6b7280',
            text: '#374151'
        };
    }, []);

    // NOUVEAU : Hook avec données centralisées
    const {
        calendarData,
        availableMonths,
        availableCategories,
        availableTypes,
        availableStatuses,
        availablePriorities,
        isLoading,
        hasError,
        error,
        updateTaskStatus,
        handleRefresh,
        getPriorityColor,
        getStatusColor,
        isFromCache,
        objectifsFromCache,
        plansFromCache
    } = useCalendarDataCached(data, hooks);

    // Filtrage des événements avec couleurs appliquées
    const filteredEvents = useMemo(() => {
        if (!calendarData || !Array.isArray(calendarData)) {
            console.warn('⚠️ calendarData n\'est pas un tableau valide:', calendarData);
            return [];
        }

        return calendarData.filter(event => {
            // Filtrage par mois
            if (activeFilterByMonth !== 'all') {
                const eventDate = new Date(event.start);
                const eventMonth = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
                if (eventMonth !== activeFilterByMonth) return false;
            }

            // Filtrage par catégorie
            if (activeFilterByCategory !== 'all' && event.category !== activeFilterByCategory) {
                return false;
            }

            // Filtrage par type
            if (activeFilterByType !== 'all' && event.type !== activeFilterByType) {
                return false;
            }

            // Filtrage par statut
            if (activeFilterByStatus !== 'all' && event.status !== activeFilterByStatus) {
                return false;
            }

            // Filtrage par priorité
            if (activeFilterByPriority !== 'all' && event.priority !== activeFilterByPriority) {
                return false;
            }

            return true;
        }).map(event => {
            // 🎨 Appliquer les couleurs selon le statut
            const statusColors = getStatusColors(event.status);
            return {
                ...event,
                backgroundColor: statusColors.background,
                color: statusColors.border,
                textColor: statusColors.text
            };
        });
    }, [calendarData, activeFilterByMonth, activeFilterByCategory, activeFilterByType, activeFilterByStatus, activeFilterByPriority, getStatusColors]);

    // Génération des jours du mois actuel
    const calendarDays = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const days = [];

        // Jours du mois précédent
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            const date = new Date(year, month, -i);
            days.push({
                date,
                isCurrentMonth: false,
                events: []
            });
        }

        // Jours du mois actuel
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayEvents = filteredEvents.filter(event => {
                const eventDate = new Date(event.start);
                return eventDate.toDateString() === date.toDateString();
            });

            days.push({
                date,
                isCurrentMonth: true,
                events: dayEvents
            });
        }

        // Compléter avec les jours du mois suivant
        const remainingDays = 42 - days.length; // 6 semaines * 7 jours
        for (let day = 1; day <= remainingDays; day++) {
            const date = new Date(year, month + 1, day);
            days.push({
                date,
                isCurrentMonth: false,
                events: []
            });
        }

        return days;
    }, [currentDate, filteredEvents]);

    // 📝 Gestion du tooltip
    const handleEventMouseEnter = useCallback((event, mouseEvent) => {
        const rect = mouseEvent.target.getBoundingClientRect();
        setTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 10
        });
        setHoveredEvent(event);
    }, []);

    const handleEventMouseLeave = useCallback(() => {
        setHoveredEvent(null);
    }, []);

    // Gestion des filtres avec fallback
    const handleFilterMonthChange = useCallback((month) => {
        if (onFilterMonthChange) {
            onFilterMonthChange(month);
        } else {
            setLocalFilterByMonth(month);
        }
    }, [onFilterMonthChange]);

    const handleFilterCategoryChange = useCallback((category) => {
        if (onFilterCategoryChange) {
            onFilterCategoryChange(category);
        } else {
            setLocalFilterByCategory(category);
        }
    }, [onFilterCategoryChange]);

    const handleFilterTypeChange = useCallback((type) => {
        if (onFilterTypeChange) {
            onFilterTypeChange(type);
        } else {
            setLocalFilterByType(type);
        }
    }, [onFilterTypeChange]);

    const handleFilterStatusChange = useCallback((status) => {
        if (onFilterStatusChange) {
            onFilterStatusChange(status);
        } else {
            setLocalFilterByStatus(status);
        }
    }, [onFilterStatusChange]);

    const handleFilterPriorityChange = useCallback((priority) => {
        if (onFilterPriorityChange) {
            onFilterPriorityChange(priority);
        } else {
            setLocalFilterByPriority(priority);
        }
    }, [onFilterPriorityChange]);

    const handleFilterReset = useCallback(() => {
        if (onFilterMonthChange) {
            onFilterMonthChange('all');
            onFilterCategoryChange('all');
            onFilterTypeChange('all');
            onFilterStatusChange('all');
            onFilterPriorityChange('all');
        } else {
            setLocalFilterByMonth('all');
            setLocalFilterByCategory('all');
            setLocalFilterByType('all');
            setLocalFilterByStatus('all');
            setLocalFilterByPriority('all');
        }
    }, [onFilterMonthChange, onFilterCategoryChange, onFilterTypeChange, onFilterStatusChange, onFilterPriorityChange]);

    // Debug logs
    console.log('🔍 Calendar Debug:', {
        calendarDataLength: calendarData?.length || 0,
        isLoading,
        hasError,
        error,
        isFromCache,
        dataObjectifsCount: data?.objectifs?.length || 0,
        dataPlansCount: data?.plans?.length || 0
    });

    // Vérification de sécurité APRÈS tous les hooks
    if (!data || (!data.objectifs && !data.plans)) {
        return (
            <div className={`h-full flex items-center justify-center ${className}`}>
                <div className="text-center text-gray-500">
                    <p>Données centralisées non fournies</p>
                </div>
            </div>
        );
    }

    // Navigation du calendrier
    const goToPreviousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Handler pour rafraîchissement
    const handleCalendarRefresh = () => {
        console.log('🔄 Rafraîchissement Calendar');
        
        // Utiliser le rafraîchissement global centralisé
        if (onGlobalRefresh && typeof onGlobalRefresh === 'function') {
            onGlobalRefresh();
        } else if (handleRefresh && typeof handleRefresh === 'function') {
            // Fallback sur le rafraîchissement local
            handleRefresh();
        }
    };

    // Formatage du mois actuel
    const currentMonthLabel = currentDate.toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric'
    });

    // Gestion du chargement
    if (isLoading && (!calendarData || calendarData.length === 0)) {
        return (
            <div className={`h-full flex items-center justify-center ${className}`}>
                <div className="text-center">
                    <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-2 animate-pulse" />
                    <div className="text-gray-600">Chargement du calendrier...</div>
                    {isFromCache && (
                        <div className="text-xs text-blue-500 mt-1">Données depuis le cache</div>
                    )}
                </div>
            </div>
        );
    }

    // Gestion des erreurs
    if (hasError) {
        return (
            <div className={`h-full flex items-center justify-center ${className}`}>
                <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
                    <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-red-800 mb-2">Erreur de chargement</h3>
                    <p className="text-sm text-red-600 mb-4">{error?.message || error}</p>
                    <button
                        onClick={handleCalendarRefresh}
                        className="px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`h-full flex flex-col overflow-hidden relative ${className}`} style={{ backgroundColor: '#f8f9fa' }}>
            {/* Tooltip pour les événements */}
            {hoveredEvent && (
                <div
                    className="fixed z-50 bg-white border border-gray-200 shadow-lg p-3 rounded-lg max-w-xs"
                    style={{
                        left: tooltipPosition.x,
                        top: tooltipPosition.y,
                        transform: 'translateX(-50%) translateY(-100%)'
                    }}
                >
                    <div className="font-semibold text-sm mb-2 text-gray-900">{hoveredEvent.title}</div>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Type:</span>
                            <span className="text-gray-700">
                                {hoveredEvent.type === 'objective' ? 'Objectif' : 
                                 hoveredEvent.type === 'task' ? 'Action' :
                                 hoveredEvent.type === 'subtask' ? 'Sous-action' : hoveredEvent.type}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Statut:</span>
                            <span className="font-medium" style={{ color: hoveredEvent.color }}>
                                {hoveredEvent.status}
                            </span>
                        </div>
                        {hoveredEvent.category && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Catégorie:</span>
                                <span className="text-gray-700">{hoveredEvent.category}</span>
                            </div>
                        )}
                        {hoveredEvent.priority && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Priorité:</span>
                                <span className="text-gray-700">
                                    {hoveredEvent.priority === 'high' ? 'Haute' :
                                     hoveredEvent.priority === 'medium' ? 'Moyenne' :
                                     hoveredEvent.priority === 'low' ? 'Basse' : hoveredEvent.priority}
                                </span>
                            </div>
                        )}
                        {hoveredEvent.estimatedHours && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Durée:</span>
                                <span className="text-gray-700">{hoveredEvent.estimatedHours}h</span>
                            </div>
                        )}
                        {hoveredEvent.description && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="text-gray-500 mb-1">Description:</div>
                                <div className="text-xs text-gray-700">{hoveredEvent.description}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Filtres */}
            {showFilters && (
                <div className="flex-shrink-0">
                    <CalendarFilters
                        filterByMonth={activeFilterByMonth}
                        filterByCategory={activeFilterByCategory}
                        filterByType={activeFilterByType}
                        filterByStatus={activeFilterByStatus}
                        filterByPriority={activeFilterByPriority}
                        availableMonths={availableMonths}
                        availableCategories={availableCategories}
                        availableTypes={availableTypes}
                        availableStatuses={availableStatuses}
                        availablePriorities={availablePriorities}
                        onFilterMonthChange={handleFilterMonthChange}
                        onFilterCategoryChange={handleFilterCategoryChange}
                        onFilterTypeChange={handleFilterTypeChange}
                        onFilterStatusChange={handleFilterStatusChange}
                        onFilterPriorityChange={handleFilterPriorityChange}
                        onReset={handleFilterReset}
                        onRefresh={handleCalendarRefresh}
                        resultCount={filteredEvents.length}
                        isLoading={isLoading}
                        isFromCache={isFromCache}
                    />
                </div>
            )}

            {/* Zone de contenu du calendrier */}
            <div className="flex-1 overflow-auto">
                <div className="p-0">
                    {(!filteredEvents || filteredEvents.length === 0) ? (
                        <div className="flex items-center justify-center py-20 text-gray-500">
                            <div className="text-center">
                                <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <div className="text-lg font-medium mb-2">
                                    {(!calendarData || calendarData.length === 0) ? 'Aucun événement disponible' : 'Aucun événement trouvé'}
                                </div>
                                <div className="text-sm mb-4">
                                    {(!calendarData || calendarData.length === 0)
                                        ? 'Créez des objectifs et des tâches avec des échéances pour voir votre calendrier'
                                        : 'Essayez de modifier vos filtres pour voir plus d\'événements'
                                    }
                                </div>
                                {calendarData && calendarData.length > 0 && (
                                    <button
                                        onClick={handleFilterReset}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                                    >
                                        Réinitialiser les filtres
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white">
                            {/* En-têtes des jours de la semaine avec le mois intégré */}
                            <div className="bg-white-100 border-b border-gray-200">
                                {/* Header avec titre du mois, bouton aujourd'hui et chevrons */}
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                                    {/* Bouton Aujourd'hui à gauche */}
                                    <button
                                        onClick={goToToday}
                                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                    >
                                        Aujourd'hui
                                    </button>

                                    {/* Titre du mois centré */}
                                    <div className="flex items-center">
                                        <h2 className="text-lg font-bold text-gray-900 capitalize">
                                            {currentMonthLabel}
                                        </h2>
                                        {isLoading && (
                                            <RefreshCw className="w-3 h-3 ml-2 animate-spin text-gray-500" />
                                        )}
                                        {isFromCache && (
                                            <span className="ml-2 text-xs text-blue-600" title="Données depuis le cache">📦</span>
                                        )}
                                    </div>

                                    {/* Chevrons de navigation à droite */}
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={goToPreviousMonth}
                                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={goToNextMonth}
                                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Jours de la semaine */}
                                <div className="grid grid-cols-7">
                                    {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                                        <div key={day} className="p-3 text-center text-sm font-medium text-gray-700">
                                            {day}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Grille du calendrier */}
                            <div className="grid grid-cols-7">
                                {calendarDays.map((day, index) => (
                                    <div
                                        key={index}
                                        className={`min-h-24 border-r border-b border-gray-100 p-2 ${
                                            !day.isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                                        } ${
                                            day.date.toDateString() === new Date().toDateString()
                                                ? 'bg-blue-50 border-blue-200'
                                                : ''
                                        }`}
                                    >
                                        <div className={`text-sm font-medium mb-1 ${
                                            !day.isCurrentMonth
                                                ? 'text-gray-400'
                                                : day.date.toDateString() === new Date().toDateString()
                                                ? 'text-blue-700'
                                                : 'text-gray-700'
                                        }`}>
                                            {day.date.getDate()}
                                        </div>

                                        {/* Événements du jour avec couleurs selon le statut */}
                                        <div className="space-y-1">
                                            {day.events.slice(0, 3).map((event, eventIndex) => (
                                                <div
                                                    key={eventIndex}
                                                    className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-90 transition-all duration-200 relative"
                                                    style={{
                                                        backgroundColor: event.backgroundColor,
                                                        color: event.textColor,
                                                        borderLeft: `3px solid ${event.color}`
                                                    }}
                                                    onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                                                    onMouseLeave={handleEventMouseLeave}
                                                >
                                                    <div className="font-medium truncate">
                                                        {event.title}
                                                    </div>
                                                </div>
                                            ))}
                                            {day.events.length > 3 && (
                                                <div className="text-xs text-gray-500 font-medium">
                                                    +{day.events.length - 3} autre{day.events.length - 3 > 1 ? 's' : ''}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CalendarSection;